/**
 * Traitements documentaires adossés aux Edge Functions.
 *
 * Les fonctions tournent dans Deno, au plus près de la base, et portent le JWT
 * de l'appelant : les policies RLS et Storage s'appliquent avec son identité.
 * Cet adaptateur n'ajoute rien à ce contrat — il traduit, et il rend les
 * erreurs lisibles.
 *
 * Ce dernier point n'est pas un confort. `functions.invoke` transforme tout
 * statut non-2xx en « Edge Function returned a non-2xx status code », en
 * jetant le corps de la réponse. Or c'est justement le corps qui dit « SIRET
 * émetteur manquant » ou « XML Factur-X non généré » — la seule information
 * qui permette au patron de corriger quoi que ce soit.
 */
import type {
  ChorusProReceipt,
  CreatedInvoice,
  DocumentPort,
  ExecutionContext,
  GeneratedFacturx,
  GeneratedPdf,
  InvoiceSendReceipt,
  QuoteSendReceipt,
  ReminderReceipt,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>;

/** Erreur d'invocation telle que supabase-js la remonte. */
interface InvokeError {
  message: string;
  context?: { json?: () => Promise<unknown> };
}

/**
 * Récupère le message métier caché dans le corps de la réponse.
 *
 * `context` est la `Response` d'origine quand supabase-js la conserve. Elle
 * peut être absente (erreur réseau) ou illisible (corps vide, HTML d'un
 * proxy) : on retombe alors sur le message générique plutôt que d'échouer une
 * seconde fois en essayant d'expliquer le premier échec.
 */
export async function edgeFunctionErrorMessage(error: InvokeError): Promise<string> {
  try {
    const body = (await error.context?.json?.()) as Payload | undefined;
    const detail = body?.error;
    if (typeof detail === "string" && detail.trim()) return detail;
  } catch {
    /* corps illisible : le message générique fera l'affaire */
  }
  return error.message;
}

export function createSupabaseDocumentPort(
  clientFor: DbClientFactory,
): DocumentPort {
  async function call<T>(
    ctx: ExecutionContext,
    fn: string,
    body: Payload,
  ): Promise<T> {
    const { data, error } = await clientFor(ctx).functions.invoke<T>(fn, { body });

    if (error) {
      throw new DomainError("conflict", await edgeFunctionErrorMessage(error as InvokeError), {
        function: fn,
      });
    }
    if (data == null) {
      throw new DomainError(
        "conflict",
        `La fonction « ${fn} » n'a rien renvoyé.`,
        { function: fn },
      );
    }
    return data;
  }

  return {
    async generatePdf(ctx, input): Promise<GeneratedPdf> {
      const r = await call<Payload>(ctx, "generate-pdf", {
        kind: input.kind,
        id: input.id,
      });
      return { url: r.pdf_url, path: r.path };
    },

    async generateFacturx(ctx, input): Promise<GeneratedFacturx> {
      const r = await call<Payload>(ctx, "generate-facturx", {
        invoice_id: input.invoiceId,
        ...(input.profile ? { profile: input.profile } : {}),
      });
      return { xmlUrl: r.xml_url ?? null, xmlLength: Number(r.xml_length ?? 0) };
    },

    async submitChorusPro(ctx, input): Promise<ChorusProReceipt> {
      const r = await call<Payload>(ctx, "submit-chorus-pro", {
        invoice_id: input.invoiceId,
      });
      return { status: String(r.status ?? "inconnu"), requestId: r.request_id ?? null };
    },

    async sendQuote(ctx, input): Promise<QuoteSendReceipt> {
      const r = await call<Payload>(ctx, "send-quote", {
        quote_id: input.quoteId,
        ...(input.message ? { message: input.message } : {}),
      });
      return {
        sent: r.sent === true,
        recorded: r.recorded !== false,
        recipient: String(r.recipient ?? ""),
        subject: String(r.subject ?? ""),
      };
    },

    async sendInvoice(ctx, input): Promise<InvoiceSendReceipt> {
      const r = await call<Payload>(ctx, "send-invoice", {
        invoice_id: input.invoiceId,
        ...(input.message ? { message: input.message } : {}),
      });
      return {
        sent: r.sent === true,
        recorded: r.recorded !== false,
        recipient: String(r.recipient ?? ""),
        subject: String(r.subject ?? ""),
      };
    },

    async sendMaterialOrder(ctx, input) {
      const r = await call<Payload>(ctx, "send-material-order", {
        order_id: input.orderId,
        ...(input.message ? { message: input.message } : {}),
      });
      return {
        sent: r.sent === true,
        recorded: r.recorded !== false,
        recipient: String(r.recipient ?? ""),
        subject: String(r.subject ?? ""),
      };
    },

    async followUpQuote(ctx, input) {
      // La même fonction que l'envoi : même pièce jointe, même historique,
      // même adresse. Le mode change le courrier et laisse le statut en paix.
      const r = await call<Payload>(ctx, "send-quote", {
        quote_id: input.quoteId,
        mode: "followup",
        ...(input.message ? { message: input.message } : {}),
      });
      return {
        sent: r.sent === true,
        recorded: r.recorded !== false,
        recipient: String(r.recipient ?? ""),
        subject: String(r.subject ?? ""),
      };
    },

    async createInvoiceFromQuote(ctx, input): Promise<CreatedInvoice> {
      const r = await call<Payload>(ctx, "create-invoice-from-quote", {
        quote_id: input.quoteId,
        type: input.type,
        ...(input.percent != null ? { percent: input.percent } : {}),
      });
      const f = r.invoice as Payload | undefined;
      if (!f?.id) {
        throw new DomainError("conflict", "La facture n'a pas été créée.", {
          quoteId: input.quoteId,
        });
      }
      return {
        id: String(f.id),
        invoiceNumber: String(f.invoice_number ?? ""),
        totalHt: Number(f.total_ht ?? 0),
        totalTtc: Number(f.total_ttc ?? 0),
        priorInvoicedHt: Number(f.prior_invoiced_ht ?? 0),
        priorInvoicedTtc: Number(f.prior_invoiced_ttc ?? 0),
      };
    },

    async sendPaymentReminder(ctx, input): Promise<ReminderReceipt> {
      const r = await call<Payload>(ctx, "send-payment-reminder", {
        invoice_id: input.invoiceId,
        level: input.level,
      });
      return {
        sent: r.sent === true,
        level: Number(r.level ?? input.level),
        ...(r.reason ? { reason: String(r.reason) } : {}),
      };
    },
  };
}
